import { useId, type ButtonHTMLAttributes, type HTMLAttributes, type ReactNode } from 'react';

function cx(...clases: (string | false | null | undefined)[]): string {
  return clases.filter(Boolean).join(' ');
}

export function Boton({
  variante = 'primario',
  tamano = 'medio',
  bloque = false,
  cargando = false,
  children,
  className,
  type = 'button',
  disabled,
  ...resto
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variante?: 'primario' | 'secundario' | 'fantasma';
  tamano?: 'medio' | 'grande';
  /** Ocupa el ancho disponible (útil en mobile o CTA de landing). */
  bloque?: boolean;
  /** Muestra spinner, bloquea la interacción y avisa a lectores de pantalla. */
  cargando?: boolean;
  children?: ReactNode;
}) {
  const inactivo = disabled || cargando;
  return (
    <button
      type={type}
      className={cx(
        'btn',
        `btn--${variante}`,
        tamano === 'grande' && 'btn--grande',
        bloque && 'btn--bloque',
        className,
      )}
      disabled={inactivo}
      aria-busy={cargando || undefined}
      data-cargando={cargando ? 'true' : undefined}
      {...resto}
    >
      {children}
    </button>
  );
}

export function Progreso({
  valor,
  etiqueta,
  mostrarPorcentaje = false,
  className,
}: {
  /** 0–1. */
  valor: number;
  /** Texto de contexto, ej. "Bloque 3 de 8". Si se pasa, se muestra sobre la barra. */
  etiqueta?: string;
  mostrarPorcentaje?: boolean;
  className?: string;
}) {
  const pct = Math.round(Math.max(0, Math.min(1, valor)) * 100);
  const conMeta = Boolean(etiqueta) || mostrarPorcentaje;
  const barra = (
    <div
      className={cx('progreso', conMeta && 'progreso--con-meta', !conMeta && className)}
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuetext={etiqueta ? etiqueta : `${pct}% completado`}
      aria-label={etiqueta ? undefined : 'Avance de la autoevaluación'}
    >
      <div className="progreso__relleno" style={{ width: `${pct}%` }} />
    </div>
  );

  if (!conMeta) return barra;

  return (
    <div className={cx('progreso-bloque', className)}>
      <div className="progreso__meta">
        {etiqueta && <span className="progreso__etiqueta">{etiqueta}</span>}
        {mostrarPorcentaje && (
          <span className="progreso__pct" aria-hidden="true">
            {pct}%
          </span>
        )}
      </div>
      {barra}
    </div>
  );
}

export function Panel({
  titulo,
  subtitulo,
  eyebrow,
  pie,
  plano = false,
  className,
  children,
}: {
  titulo?: string;
  subtitulo?: string;
  /** Línea corta en mayúsculas sobre el título. */
  eyebrow?: string;
  /** Zona inferior separada, para acciones persistentes o notas legales. */
  pie?: ReactNode;
  /** Sin barra de acento ni sombra fuerte, para paneles secundarios. */
  plano?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const idTitulo = useId();
  const conCabecera = Boolean(titulo || subtitulo || eyebrow);
  return (
    <section
      className={cx('panel', plano && 'panel--plano', className)}
      aria-labelledby={titulo ? idTitulo : undefined}
    >
      {conCabecera && (
        <header className="panel__cabecera">
          {eyebrow && <span className="panel__eyebrow">{eyebrow}</span>}
          {titulo && (
            <h2 className="panel__titulo" id={idTitulo}>
              {titulo}
            </h2>
          )}
          {subtitulo && <p className="panel__subtitulo">{subtitulo}</p>}
        </header>
      )}
      <div className="panel__cuerpo">{children}</div>
      {pie && <div className="panel__pie">{pie}</div>}
    </section>
  );
}

export function Badge({
  tono = 'marca',
  children,
  className,
  ...resto
}: HTMLAttributes<HTMLSpanElement> & {
  tono?: 'marca' | 'acento' | 'neutro';
  children: ReactNode;
}) {
  const sufijo = tono === 'marca' ? '' : ` badge--${tono}`;
  return (
    <span className={cx(`badge${sufijo}`, className)} {...resto}>
      {children}
    </span>
  );
}

/** Indicador textual de posición, ej. "Paso 3 / 8". */
export function StepLabel({
  actual,
  total,
  prefijo = 'Paso',
  className,
}: {
  actual: number;
  total: number;
  prefijo?: string;
  className?: string;
}) {
  return (
    <p className={cx('step-label', className)}>
      <span>{prefijo}</span>
      <span>
        <span className="step-label__actual">{actual}</span> / {total}
      </span>
    </p>
  );
}
